/**
 * Страховка /orders от потерянного серверного события: один таймер на
 * ближайший известный дедлайн. Чистый модуль без React / PRPC, чтобы Jest
 * гонял тот же код, что и `Order.subscribe`. Что делать с ответом getById
 * решает ordersHydrate.
 */

import { clampTimeoutDelay, offerDeadlineMs } from "./ordersHotPath";
import { StaleOrderSnapshot } from "./ordersHydrate";

/** distribution тикает раз в 4 с, поэтому к дедлайну оффера даём запас. */
export const STALE_OFFER_GRACE_MS = 6_000;
/** accept: оффер уже снят, executorToOrder ещё не записан. */
export const STALE_ACCEPT_GRACE_MS = 5_000;
/** Повторная проверка одного заказа: от стольких с backoff ×2 ... */
export const STALE_RECHECK_MS = 15_000;
/** ...до стольких, если сервер упорно отдаёт тот же снапшот. */
export const STALE_RECHECK_MAX_MS = 120_000;

export type StaleWatchdogOptions = {
	getModels: () => unknown[];
	/** false — таймер не взводится (disposed, closed-таб, countOnly) */
	isActive: () => boolean;
	enqueueHydrate: (id: number) => void;
	now?: () => number;
	offerGraceMs?: number;
	acceptGraceMs?: number;
	recheckMs?: number;
	recheckMaxMs?: number;
};

export type StaleWatchdog = {
	/** Перевзвести таймер на ближайший дедлайн по текущему списку. */
	arm: () => void;
	dispose: () => void;
};

/**
 * Таймер взводится только на ближайший известный дедлайн (оффер,
 * зависший accept), а не опрашивает весь список по интервалу.
 * Сработал — точечный hydrate тех, чей срок вышел.
 */
export const createStaleWatchdog = (
	options: StaleWatchdogOptions,
): StaleWatchdog => {
	const now = options.now ?? Date.now;
	const offerGraceMs = options.offerGraceMs ?? STALE_OFFER_GRACE_MS;
	const acceptGraceMs = options.acceptGraceMs ?? STALE_ACCEPT_GRACE_MS;
	const recheckMs = options.recheckMs ?? STALE_RECHECK_MS;
	const recheckMaxMs = options.recheckMaxMs ?? STALE_RECHECK_MAX_MS;

	let handle: ReturnType<typeof setTimeout> | null = null;
	let disposed = false;
	const acceptSeenAt = new Map<number, number>();
	/** id → [когда проверяли, сколько раз подряд, по какому дедлайну] */
	const checkedAt = new Map<number, [number, number, number]>();

	const clear = () => {
		if (handle != null) {
			clearTimeout(handle);
			handle = null;
		}
	};

	/**
	 * Дедлайн строки и её «база» для backoff. База стабильна между
	 * пересылками одного и того же оффера (серверный orderOfferTime), а
	 * локальный дедлайн после каждого снапшота плавает на джиттер задержки.
	 */
	const staleDeadline = (
		item: StaleOrderSnapshot,
		at: number,
	): { deadline: number; base: number } | null => {
		const id = Number(item?.id);
		if (!id) return null;
		let deadline: number | null = null;
		let base = 0;
		const offerDeadline = offerDeadlineMs(item, at);
		if (typeof offerDeadline === "number") {
			acceptSeenAt.delete(id);
			deadline = offerDeadline + offerGraceMs;
			base = item.offer?.orderOfferTime ?? offerDeadline;
		} else if (
			item.executingStages?.accepted &&
			!item.executorToOrder?.length &&
			!item.offer
		) {
			const seenAt = acceptSeenAt.get(id) ?? at;
			acceptSeenAt.set(id, seenAt);
			deadline = seenAt + acceptGraceMs;
			base = seenAt;
		}
		if (deadline == null) {
			acceptSeenAt.delete(id);
			checkedAt.delete(id);
			return null;
		}
		const lastCheck = checkedAt.get(id);
		if (lastCheck == null) return { deadline, base };
		const [checkedAtMs, attempts, lastBase] = lastCheck;
		// новый оффер / новый accept — backoff с нуля
		if (lastBase !== base) {
			checkedAt.delete(id);
			return { deadline, base };
		}
		const backoff = Math.min(
			recheckMs * 2 ** Math.max(0, attempts - 1),
			recheckMaxMs,
		);
		return { deadline: Math.max(deadline, checkedAtMs + backoff), base };
	};

	function run() {
		handle = null;
		if (disposed || !options.isActive()) return;
		const at = now();
		for (const item of options.getModels() as StaleOrderSnapshot[]) {
			const stale = staleDeadline(item, at);
			if (stale != null && stale.deadline <= at) {
				const id = Number(item.id);
				const prev = checkedAt.get(id);
				checkedAt.set(id, [at, (prev?.[1] ?? 0) + 1, stale.base]);
				options.enqueueHydrate(id);
			}
		}
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		arm();
	}

	function arm() {
		clear();
		if (disposed || !options.isActive()) return;
		const at = now();
		const alive = new Set<number>();
		let next = Infinity;
		for (const item of options.getModels() as StaleOrderSnapshot[]) {
			const stale = staleDeadline(item, at);
			alive.add(Number(item?.id));
			if (stale != null && stale.deadline < next) next = stale.deadline;
		}
		acceptSeenAt.forEach((_, id) => {
			if (!alive.has(id)) acceptSeenAt.delete(id);
		});
		checkedAt.forEach((_, id) => {
			if (!alive.has(id)) checkedAt.delete(id);
		});
		if (next === Infinity) return;
		handle = setTimeout(run, clampTimeoutDelay(next - at));
	}

	return {
		arm,
		dispose: () => {
			disposed = true;
			clear();
			acceptSeenAt.clear();
			checkedAt.clear();
		},
	};
};
